import { useState, useEffect } from 'react';
import {
  Key,
  Plus,
  Trash,
  Copy,
  RefreshDouble,
  Check,
  Xmark,
  WarningTriangle,
  Clock,
} from 'iconoir-react';
import type { ApiKey } from '../../lib/iam-api';
import {
  fetchApiKeys,
  fetchApiKeyStats,
  createApiKey,
  regenerateApiKey,
  deleteApiKey,
  markApiKeyCopied,
} from '../../lib/iam-api';

export function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyDisplay, setNewKeyDisplay] = useState<{ key: string; name: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [keys, statsData] = await Promise.all([
        fetchApiKeys(),
        fetchApiKeyStats(),
      ]);
      // Ensure keys is always an array
      setApiKeys(Array.isArray(keys) ? keys : []);
      setStats(statsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load API keys');
      setApiKeys([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async (apiKey: ApiKey) => {
    if (!confirm(`Regenerating will invalidate the current key. Continue?`)) {
      return;
    }
    try {
      const result = await regenerateApiKey(apiKey.id);
      setNewKeyDisplay({ key: result.plainKey, name: apiKey.name });
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (apiKey: ApiKey) => {
    if (!confirm(`Are you sure you want to delete "${apiKey.name}"?`)) {
      return;
    }
    try {
      await deleteApiKey(apiKey.id);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCopyKey = async (key: string, id?: string) => {
    await navigator.clipboard.writeText(key);
    if (id) {
      await markApiKeyCopied(id);
    }
    // Show brief copy feedback
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg">
              <Key className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
              <p className="text-sm text-gray-600">Manage programmatic access to ThiranAI</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Generate Key
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Keys" value={stats.total} color="blue" />
            <StatCard label="Active" value={stats.active} color="green" />
            <StatCard label="Expired" value={stats.expired} color="red" />
            <StatCard label="Never Expires" value={stats.neverExpires} color="purple" />
          </div>
        )}

        {/* New Key Display */}
        {newKeyDisplay && (
          <div className="mb-6 p-6 bg-green-50 border-2 border-green-200 rounded-2xl">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-green-800 mb-1">
                  API Key Generated: {newKeyDisplay.name}
                </h3>
                <p className="text-sm text-green-700 mb-3">
                  Save this key now. You won't be able to see it again!
                </p>
                <div className="flex items-center gap-2 bg-white p-3 rounded-lg border border-green-200">
                  <code className="flex-1 text-sm font-mono text-gray-800 break-all">
                    {newKeyDisplay.key}
                  </code>
                  <button
                    onClick={() => handleCopyKey(newKeyDisplay.key)}
                    className="p-2 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                  >
                    <Copy className="w-5 h-5 text-green-600" />
                  </button>
                </div>
              </div>
              <button
                onClick={() => setNewKeyDisplay(null)}
                className="p-1 hover:bg-green-100 rounded"
              >
                <Xmark className="w-5 h-5 text-green-600" />
              </button>
            </div>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
            <WarningTriangle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* API Keys Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Your API Keys</h2>
            <button
              onClick={loadData}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshDouble className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-amber-500 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Loading API keys...</p>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="p-12 text-center">
              <Key className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No API keys yet</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-amber-600 hover:text-amber-700 font-medium"
              >
                Generate your first API key
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {apiKeys.map((apiKey) => (
                <div key={apiKey.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{apiKey.name}</h3>
                        {apiKey.isActive ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                            Inactive
                          </span>
                        )}
                        {apiKey.neverExpires && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                            Never Expires
                          </span>
                        )}
                      </div>
                      {apiKey.description && (
                        <p className="text-sm text-gray-500 mb-2">{apiKey.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                          {apiKey.keyPreview}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {apiKey.lastUsedAt
                            ? `Used ${new Date(apiKey.lastUsedAt).toLocaleDateString()}`
                            : 'Never used'}
                        </span>
                        <span>Usage: {apiKey.usageCount}</span>
                        {apiKey.expiresAt && (
                          <span className={new Date(apiKey.expiresAt) < new Date() ? 'text-red-500' : ''}>
                            Expires: {new Date(apiKey.expiresAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRegenerate(apiKey)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Regenerate key"
                      >
                        <RefreshDouble className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(apiKey)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete key"
                      >
                        <Trash className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create API Key Modal */}
        {showCreateModal && (
          <CreateApiKeyModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={(key, name) => {
              setShowCreateModal(false);
              setNewKeyDisplay({ key, name });
              loadData();
            }}
          />
        )}
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorClasses: Record<string, string> = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    red: 'from-red-500 to-red-600',
    purple: 'from-purple-500 to-purple-600',
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-md">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold bg-gradient-to-r ${colorClasses[color]} bg-clip-text text-transparent`}>
        {value}
      </p>
    </div>
  );
}

// Create API Key Modal
function CreateApiKeyModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (key: string, name: string) => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    neverExpires: true,
    expiresAt: '',
    scopes: ['query:read', 'query:write', 'schema:extract'] as string[],
    appScopes: [] as string[], // App-specific scopes
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await createApiKey({
        name: formData.name,
        description: formData.description || undefined,
        neverExpires: formData.neverExpires,
        expiresAt: formData.neverExpires ? undefined : formData.expiresAt,
        scopes: formData.scopes,
        appScopes: formData.appScopes.length > 0 ? formData.appScopes : undefined,
      });
      onSuccess(result.plainKey, formData.name);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const availableScopes = [
    { value: 'query:read', label: 'Read Queries' },
    { value: 'query:write', label: 'Write Queries' },
    { value: 'schema:extract', label: 'Extract Schema' },
    { value: 'documents:read', label: 'Read Documents' },
    { value: 'documents:write', label: 'Write Documents' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Generate API Key</h2>
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              placeholder="e.g., Production API"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              placeholder="What is this key used for?"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.neverExpires}
                onChange={(e) => setFormData({ ...formData, neverExpires: e.target.checked })}
                className="rounded text-amber-600 focus:ring-amber-500"
              />
              <span className="text-sm text-gray-700">Never expires</span>
            </label>
          </div>
          {!formData.neverExpires && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
              <input
                type="date"
                value={formData.expiresAt}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
            <div className="space-y-2">
              {availableScopes.map((scope) => (
                <label key={scope.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.scopes.includes(scope.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, scopes: [...formData.scopes, scope.value] });
                      } else {
                        setFormData({
                          ...formData,
                          scopes: formData.scopes.filter((s) => s !== scope.value),
                        });
                      }
                    }}
                    className="rounded text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">{scope.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App Scope (Leave empty for all apps)
            </label>
            <div className="space-y-2">
              {['admin', 'iam', 'training', 'demo', 'experimental'].map((app) => (
                <label key={app} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.appScopes.includes(app)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, appScopes: [...formData.appScopes, app] });
                      } else {
                        setFormData({
                          ...formData,
                          appScopes: formData.appScopes.filter((s) => s !== app),
                        });
                      }
                    }}
                    className="rounded text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700 capitalize">{app}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              If no apps are selected, this key will work for ALL applications
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate Key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
